import { defineComponent, ref, computed, inject, watch, onBeforeMount, onMounted, onBeforeUnmount } from 'vue';
import Highcharts from 'highcharts';
import accessibility from 'highcharts/modules/accessibility.js';
import set from 'lodash.set';
import throttle from 'lodash.throttle';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import { useLogger } from '../../composables/logger/index.js';
import UiButtonGroup from '../ui-button-group/UiButtonGroup.vue.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import { wrapChartOnRender } from '../../helpers/component.js';
import { generateElementId } from '../../helpers/generators.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "PortfolioEvaluationChart",
  props: {
    locale: { type: null, required: true },
    id: { type: null, required: true },
    initialTimespan: { type: null, required: false },
    timeSpans: { type: Array, required: false, default: () => ["3m", "1y", "1stTrade"] },
    updateFrequency: { type: Number, required: false, default: 2e3 }
  },
  emits: ["store-error", "data-load"],
  setup(__props, { emit: emits }) {
    var _a;
    const props = __props;
    accessibility(Highcharts);
    const MINIMUM_TICKS = 3;
    const chartContainer = ref();
    let chart = null;
    const chartId = generateElementId("fds-portfolio-evaluation-chart-");
    const isLoadingChartData = ref(true);
    const selectedTimeSpan = ref((_a = props.initialTimespan) != null ? _a : null);
    const componentInputs = computed(() => {
      var _a2;
      return {
        id: props.id,
        language: props.locale,
        timeSpan: (_a2 = selectedTimeSpan.value) != null ? _a2 : "3m"
      };
    });
    const isChartLoading = computed(() => isLoadingStoreData.value || isLoadingChartData.value);
    const seriesData = computed(() => {
      var _a2, _b;
      return (_b = (_a2 = storeData.value) == null ? void 0 : _a2.portfolioValue) != null ? _b : [];
    });
    const timeSpanButtons = computed(() => {
      return props.timeSpans.map((timeSpan) => ({
        key: timeSpan,
        label: translate(`portfolioEvaluationChart.${timeSpan}`)
      }));
    });
    const range = computed(() => {
      var _a2, _b;
      if (seriesData.value.length === 0) {
        return {
          start: null,
          end: null
        };
      }
      const startPoint = (_a2 = seriesData.value[0]) != null ? _a2 : null;
      const endPoint = (_b = seriesData.value[seriesData.value.length - 1]) != null ? _b : null;
      return {
        start: startPoint ? new Date(startPoint[0]) : null,
        end: endPoint ? new Date(endPoint[0]) : null
      };
    });
    const showNavigation = computed(() => {
      return (props.timeSpans.length || Number.NaN) > 1;
    });
    const changeTimeSpan = (timeSpan) => {
      selectedTimeSpan.value = timeSpan;
    };
    const localizationSupport = useLocalizationSupport(props, {
      translator: inject("translator"),
      numberFormatter: inject("numberFormatter"),
      dateFormatter: inject("dateFormatter")
    });
    const { translate } = localizationSupport;
    const storeModule = useStoreModule(
      { storeModule: inject("storeModule") },
      componentInputs,
      emits,
      { requestDataOnComponentInputsChange: false }
    );
    const { storeData, isLoadingStoreData, storeCleanUp, storeRequestData } = storeModule;
    const loggerComposable = useLogger();
    const { getLogger } = loggerComposable;
    const highChartOptionsParams = ref({
      range: range.value,
      localizationSupport
    });
    const init = () => {
      if (chartContainer.value) {
        const chartConfig = getChartConfig();
        if (seriesData.value.length > MINIMUM_TICKS) {
          set(chartConfig, "series[0].data", seriesData);
        }
        chart = Highcharts.chart(chartContainer.value, chartConfig);
      }
    };
    const onRender = () => {
      isLoadingChartData.value = false;
    };
    const getChartConfig = () => wrapChartOnRender(highChartOptions ? highChartOptions(highChartOptionsParams.value) : {}, onRender);
    const setSeriesData = () => {
      if (chart) {
        chart.update(getChartConfig());
        const currentSeriesData = seriesData.value.length > MINIMUM_TICKS ? seriesData.value : [];
        chart.series[0].setData(currentSeriesData, true);
      }
    };
    const highChartOptions = inject("highChartOptions");
    const throttledSetMapData = throttle(setSeriesData, props.updateFrequency);
    watch(
      seriesData,
      () => {
        throttledSetMapData();
      },
      {
        immediate: true
      }
    );
    const updateComponentData = async () => {
      isLoadingChartData.value = true;
      await storeRequestData();
      setSeriesData();
    };
    onBeforeMount(() => {
      if (props.initialTimespan) {
        changeTimeSpan(props.initialTimespan);
      } else if (props.timeSpans.length > 0) {
        changeTimeSpan(props.timeSpans[0]);
      } else {
        const logger = getLogger();
        logger.warn("No selected timespan.");
      }
    });
    onMounted(() => {
      init();
      void updateComponentData();
    });
    onBeforeUnmount(() => {
      chart == null ? void 0 : chart.destroy();
      chart = null;
    });
    watch(
      componentInputs,
      (_, oldComponentInputs) => {
        void storeCleanUp(oldComponentInputs).then(updateComponentData);
      },
      { deep: true }
    );
    return { __sfc: true, MINIMUM_TICKS, chartContainer, chart, chartId, isLoadingChartData, props, selectedTimeSpan, emits, componentInputs, isChartLoading, seriesData, timeSpanButtons, range, showNavigation, changeTimeSpan, localizationSupport, translate, storeModule, storeData, isLoadingStoreData, storeCleanUp, storeRequestData, loggerComposable, getLogger, highChartOptionsParams, init, onRender, getChartConfig, setSeriesData, highChartOptions, throttledSetMapData, updateComponentData, UiButtonGroup, HoistComponent };
  }
});

export { _sfc_main as default };
